import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { type ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { useGetPayoutSettings } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetPayoutSettings";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { PageContent } from "../../WorkerShifts/components/PageContent";
import { useCancelledShiftDetails } from "../api/useCancelledShiftDetails";
import { GUIDED_SUPPORT_FULL_PATH } from "../paths";
import { ErrorState } from "../ShiftDetails/components/ErrorState";
import { LoadingState } from "../ShiftDetails/components/LoadingState";
import { type ShiftDetailsRouteParams } from "../types";
import { CancelledShiftDetailsContent } from "./components/CancelledShiftDetailsContent";

export function CancelledShiftDetailsPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { shiftId } = useParams<ShiftDetailsRouteParams>();

  const { data, isLoading, isError, refetch } = useCancelledShiftDetails({ shiftId });

  // Fetched eagerly (not gated on eligibility) because payout settings are user-level
  // and likely already cached from other pages. Only displayed when eligible.
  const { data: payoutSettings } = useGetPayoutSettings({
    includeExternalAccount: true,
  });
  const payoutDestination = payoutSettings?.destinationDetails;

  useEffect(() => {
    if (isDefined(data)) {
      logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_SHIFT_DETAILS_VIEWED, {
        shiftId,
        isCancelled: true,
        cancellationType: data.cancellationType,
      });
    }
  }, [shiftId, data]);

  const { showErrorToast } = useToast();

  const handleReportIssue = useCallback(async () => {
    logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_REPORT_ISSUE_TAPPED, {
      shiftId,
      isCancelled: true,
    });
    try {
      await openZendeskMessaging();
    } catch {
      showErrorToast("Unable to open support chat. Please try again.");
    }
  }, [shiftId, showErrorToast]);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          fallbackGoBackPath={GUIDED_SUPPORT_FULL_PATH}
          headerVariant="tertiary"
          scrollContainerRef={scrollRef}
          title="Cancelled Shift"
        />
        <PageContent
          ref={scrollRef}
          sx={(theme) => ({
            flex: 1,
            padding: theme.spacing(4),
          })}
        >
          <CancelledShiftPageContent
            data={data}
            handleRetry={handleRetry}
            isError={isError}
            isLoading={isLoading}
            payoutDestination={payoutDestination}
          />
        </PageContent>

        <PageFooterWrapper>
          <LoadingButton
            fullWidth
            isLoading={false}
            size="large"
            variant="contained"
            onClick={handleReportIssue}
          >
            Report an Issue
          </LoadingButton>
        </PageFooterWrapper>
      </PageWrapper>
    </ThemeProvider>
  );
}

interface CancelledShiftPageContentProps {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly data: ReturnType<typeof useCancelledShiftDetails>["data"];
  readonly handleRetry: () => void;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
}

function CancelledShiftPageContent(props: CancelledShiftPageContentProps) {
  const { isLoading, isError, data, handleRetry, payoutDestination } = props;

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError || !isDefined(data)) {
    return <ErrorState onRetry={handleRetry} />;
  }

  return <CancelledShiftDetailsContent data={data} payoutDestination={payoutDestination} />;
}
